import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {
    getStoredSession,
    setStoredSession,
    updateUser
} from '../../lib/supabase-auth.js';

import styles from './ai-sidebar.css';
import {ALL_BLOCKS} from '../../lib/bitbyte-blocks-data.js';

const API_URL = 'https://bitbiteapi.namrahaijaz87.workers.dev/api/ask';

const BitByteBlock = ({opcode, params}) => {
    const block = ALL_BLOCKS[opcode];

    if (!block) {
        console.error('BitByteBlock: Block data not found for opcode:', opcode);
        return (
            <div className={styles.errorBox}>
                Unknown block: {opcode}
            </div>
        );
    }

    const renderValue = (value, type) => {
        if (Array.isArray(value)) {
            return (
                <div className={styles.substack}>
                    {value.map((b, i) => (
                        <BitByteBlock key={i} opcode={b.opcode} params={b.params} />
                    ))}
                </div>
            );
        }

        if (typeof value === 'object' && value !== null && value.opcode) {
            return <BitByteBlock opcode={value.opcode} params={value.params} />;
        }

        // Human-friendly labels for special values
        let displayValue = String(value ?? '');
        if (displayValue === '_random_') displayValue = 'random position';
        if (displayValue === '_mouse_') displayValue = 'mouse-pointer';
        if (displayValue === '_stage_') displayValue = 'Stage';
        if (displayValue === '_edge_') displayValue = 'edge';

        return (
            <span className={type === 'boolean' ? styles.blockInputBooleanValue : styles.blockInput}>
                {displayValue || '\u00A0'}
            </span>
        );
    };

    const renderText = text => {
        if (!text) return <span>{opcode}</span>;

        // Split by placeholders like [INPUT], <CONDITION>, or ... (for else)
        const parts = text.split(/(\[[^\]]+\]|<[^>]+>|\.\.\.)/);

        return parts.map((part, i) => {
            if (!part) return null;

            // Handle square bracket inputs [VAR]
            if (part.startsWith('[') && part.endsWith(']')) {
                const inputKey = part.slice(1, -1);
                const inputData = (block.inputs && typeof block.inputs === 'object')
                    ? block.inputs[inputKey]
                    : null;
                const value = (params && params[inputKey] !== undefined)
                    ? params[inputKey]
                    : (inputData ? inputData.default : '');

                return <React.Fragment key={`input-${i}`}>{renderValue(value, inputData?.type)}</React.Fragment>;
            }

            // Handle angle bracket booleans <BOOL>
            if (part.startsWith('<') && part.endsWith('>')) {
                const inputKey = part.slice(1, -1);
                const value = (params && params[inputKey] !== undefined) ? params[inputKey] : null;

                if (value && typeof value === 'object' && value.opcode) {
                    return <BitByteBlock key={`bool-${i}`} opcode={value.opcode} params={value.params} />;
                }

                return <span key={`bool-empty-${i}`} className={styles.blockInputBoolean} />;
            }

            // Handle else placeholder
            if (part === '...') return null;

            // Plain text
            return <span key={`text-${i}`}>{part}</span>;
        });
    };

    const blockClass = classNames(styles.block, {
        [styles.blockHat]: block.blockType === 'hat',
        [styles.blockStack]: block.blockType === 'stack',
        [styles.blockReporter]: block.blockType === 'reporter',
        [styles.blockBoolean]: block.blockType === 'boolean',
        [styles.blockCap]: block.blockType === 'cap',
        [styles.blockC]: block.inputs && (block.inputs.SUBSTACK || block.inputs.SUBSTACK2)
    });

    const isHat = block.blockType === 'hat';

    return (
        <React.Fragment>
            <div
                className={blockClass}
                style={{backgroundColor: block.color || '#4C97FF'}}
            >
                <div className={styles.blockTop}>
                    {renderText(block.text)}
                </div>
                {!isHat && params?.SUBSTACK && (
                    <div className={styles.substack}>
                        {params.SUBSTACK.map((b, i) => (
                            <BitByteBlock key={`sub-${i}`} opcode={b.opcode} params={b.params} />
                        ))}
                    </div>
                )}
                {!isHat && block.text.includes('else') && (
                    <div className={styles.blockTop}>else</div>
                )}
                {!isHat && params?.SUBSTACK2 && (
                    <div className={styles.substack}>
                        {params.SUBSTACK2.map((b, i) => (
                            <BitByteBlock key={`sub2-${i}`} opcode={b.opcode} params={b.params} />
                        ))}
                    </div>
                )}
            </div>
            {isHat && params?.SUBSTACK && params.SUBSTACK.map((b, idx) => (
                <BitByteBlock
                    key={`hat-sub-${idx}`}
                    opcode={b.opcode}
                    params={b.params}
                />
            ))}
        </React.Fragment>
    );
};

BitByteBlock.propTypes = {
    opcode: PropTypes.string.isRequired,
    params: PropTypes.object
};

const BitByteBlockFrame = ({content}) => {
    const blocks = [];

    // Recursive parser for BLOCK(opcode, params)
    let i = 0;
    const parseValue = () => {
        while (i < content.length && /\s/.test(content[i])) i++;

        if (content.startsWith('BLOCK(', i)) {
            return parseOneBlock();
        }

        if (content[i] === '[') {
            i++; // skip [
            const arr = [];
            while (i < content.length && content[i] !== ']') {
                const val = parseValue();
                if (val !== undefined) arr.push(val);
                while (i < content.length && (/\s/.test(content[i]) || content[i] === ',')) i++;
            }
            if (content[i] === ']') i++;
            // Always return an array, even if empty, so the renderer knows to show the substack area
            return arr;
        }

        let start = i;
        if (content[i] === '"' || content[i] === "'") {
            const quote = content[i];
            i++;
            start = i;
            while (i < content.length && content[i] !== quote) i++;
            const val = content.slice(start, i);
            if (content[i] === quote) i++;
            return val;
        }

        while (i < content.length && !/[,\)\]]/.test(content[i])) i++;
        const raw = content.slice(start, i).trim();
        if (raw === '') return undefined;
        // Convert numbers
        if (!isNaN(raw)) return Number(raw);
        return raw;
    };

    const parseOneBlock = () => {
        i += 6; // skip BLOCK(
        while (i < content.length && /\s/.test(content[i])) i++;
        let start = i;
        while (i < content.length && !/[,\)]/.test(content[i])) i++;
        const opcode = content.slice(start, i).trim();

        const params = {};
        const blockDef = ALL_BLOCKS[opcode];
        const inputKeys = blockDef && blockDef.inputs ? Object.keys(blockDef.inputs) : [];
        let paramIndex = 0;

        while (i < content.length && content[i] === ',') {
            i++; // skip comma
            while (i < content.length && /\s/.test(content[i])) i++;

            let keyMatch = content.slice(i).match(/^([a-zA-Z0-9_-]+)\s*:/);
            if (keyMatch) {
                const key = keyMatch[1];
                i += keyMatch[0].length;
                params[key] = parseValue();
            } else {
                const val = parseValue();
                if (val !== undefined) {
                    let key = inputKeys[paramIndex];

                    // If the value is an array, it's likely a SUBSTACK
                    if (Array.isArray(val)) {
                        if (params.SUBSTACK) {
                            key = 'SUBSTACK2';
                        } else {
                            key = 'SUBSTACK';
                        }
                    }

                    if (key) {
                        params[key] = val;
                    } else {
                        params[`arg${paramIndex}`] = val;
                    }
                    paramIndex++;
                }
            }
            while (i < content.length && /\s/.test(content[i])) i++;
        }
        if (content[i] === ')') i++;
        return {opcode, params};
    };

    while (i < content.length) {
        while (i < content.length && !content.startsWith('BLOCK(', i)) i++;
        if (i < content.length) {
            blocks.push(parseOneBlock());
        }
    }

    if (blocks.length === 0) {
        return <span className={styles.inlineCode}>{content}</span>;
    }

    return (
        <span className={styles.blockFrame}>
            {blocks.map((b, idx) => (
                <BitByteBlock
                    key={`${b.opcode}-${idx}`}
                    opcode={b.opcode}
                    params={b.params}
                />
            ))}
        </span>
    );
};

BitByteBlockFrame.propTypes = {
    content: PropTypes.string.isRequired
};

const CONVERSATIONS_STORAGE_KEY = 'bitbyte.ai.conversations';

const getStoredConversations = () => {
    if (typeof window === 'undefined') return [];
    try {
        const val = window.localStorage.getItem(CONVERSATIONS_STORAGE_KEY);
        return val ? JSON.parse(val) : [];
    } catch (e) {
        return [];
    }
};

const setStoredConversations = convs => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(CONVERSATIONS_STORAGE_KEY, JSON.stringify(convs));
    } catch (e) {}
};

const MODEL_COSTS = {
    mini: 10,
    normal: 15,
    reasoning: 25
};

const quickIdeas = [
    'Explain this block',
    'Help me fix a sprite',
    'Brainstorm a game',
    'Turn this into a story'
];

const modelOptions = [
    {label: 'Normal', value: 'normal'},
    {label: 'Mini', value: 'mini'},
    {label: 'Reasoning', value: 'reasoning'}
];

const getSessionCredits = session => {
    const parsed = Number(session?.user?.user_metadata?.credits);
    return Number.isFinite(parsed) ? parsed : 200;
};

const markdownInlinePatterns = [
    {
        regex: /FRAME\s*\{([\s\S]*?)\}/,
        render: (content, key) => <BitByteBlockFrame key={key} content={content} />
    },
    {
        regex: /`([^`]+)`/,
        render: (content, key) => <code key={key} className={styles.inlineCode}>{content}</code>
    },
    {
        regex: /\*\*([^*]+)\*\*/,
        render: (content, key) => <strong key={key}>{content}</strong>
    },
    {
        regex: /\*([^*]+)\*/,
        render: (content, key) => <em key={key}>{content}</em>
    },
    {
        regex: /\[([^\]]+)\]\(([^)]+)\)/,
        render: (content, key, match) => (
            <a
                key={key}
                href={match[2]}
                rel="noreferrer"
                target="_blank"
            >
                {content}
            </a>
        )
    }
];

const renderInlineMarkdown = text => {
    let remaining = text;
    const nodes = [];
    let index = 0;

    while (remaining.length > 0) {
        let matched = false;
        for (const pattern of markdownInlinePatterns) {
            const match = remaining.match(pattern.regex);
            if (match && match.index === 0) {
                nodes.push(pattern.render(match[1], `md-${index++}`, match));
                remaining = remaining.slice(match[0].length);
                matched = true;
                break;
            }
        }

        if (!matched) {
            const nextSpecial = remaining.search(/[`*\[$\n]/);
            if (nextSpecial === -1) {
                nodes.push(remaining);
                break;
            }
            if (nextSpecial > 0) {
                nodes.push(remaining.slice(0, nextSpecial));
                remaining = remaining.slice(nextSpecial);
            } else if (remaining[0] === '\n') {
                nodes.push(<br key={`br-${index++}`} />);
                remaining = remaining.slice(1);
            } else {
                nodes.push(remaining[0]);
                remaining = remaining.slice(1);
            }
        }
    }

    return nodes;
};

const renderMarkdown = text => {
    const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
    const blocks = [];
    let codeLines = [];
    let inCodeBlock = false;
    let inFrame = false;
    let frameLines = [];
    let listType = null;
    let listItems = [];
    let paragraphLines = [];

    const flushParagraph = () => {
        if (!paragraphLines.length) return;
        const fullText = paragraphLines.join('\n');
        blocks.push(
            <p key={`p-${blocks.length}`} className={styles.markdownParagraph}>
                {renderInlineMarkdown(fullText)}
            </p>
        );
        paragraphLines = [];
    };

    const flushList = () => {
        if (!listItems.length) return;
        const ListTag = listType === 'ordered' ? 'ol' : 'ul';
        blocks.push(
            <ListTag key={`${ListTag}-${blocks.length}`} className={styles.markdownList}>
                {listItems.map((item, itemIndex) => (
                    <li
                        key={`${ListTag}-item-${itemIndex}`}
                        className={styles.markdownListItem}
                    >
                        {renderInlineMarkdown(item)}
                    </li>
                ))}
            </ListTag>
        );
        listItems = [];
        listType = null;
    };

    const flushCode = () => {
        if (!codeLines.length) return;
        blocks.push(
            <pre key={`code-${blocks.length}`} className={styles.markdownCodeBlock}>
                <code>{codeLines.join('\n')}</code>
            </pre>
        );
        codeLines = [];
    };

    const flushFrame = () => {
        if (!frameLines.length) return;
        const frameContent = frameLines.join('\n');
        // Extract content between FRAME { and }
        const match = frameContent.match(/FRAME\s*\{([\s\S]*?)\}/);
        if (match) {
            blocks.push(<BitByteBlockFrame key={`frame-${blocks.length}`} content={match[1]} />);
        } else {
            // Fallback: render as paragraph if regex fails
            blocks.push(<p key={`p-${blocks.length}`}>{renderInlineMarkdown(frameContent)}</p>);
        }
        frameLines = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trimEnd();

        // Handle multi-line FRAME blocks
        if (line.includes('FRAME {') && !inCodeBlock) {
            flushParagraph();
            flushList();
            inFrame = true;
            frameLines.push(line);
            if (line.includes('}')) {
                flushFrame();
                inFrame = false;
            }
            continue;
        }

        if (inFrame) {
            frameLines.push(line);
            if (line.includes('}')) {
                flushFrame();
                inFrame = false;
            }
            continue;
        }

        if (line.startsWith('```')) {
            if (inCodeBlock) {
                flushCode();
                inCodeBlock = false;
            } else {
                flushParagraph();
                flushList();
                inCodeBlock = true;
            }
            continue;
        }

        if (inCodeBlock) {
            codeLines.push(rawLine);
            continue;
        }

        if (!line.trim()) {
            flushParagraph();
            flushList();
            continue;
        }

        const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
        if (headingMatch) {
            flushParagraph();
            flushList();
            const level = headingMatch[1].length;
            const HeadingTag = `h${Math.min(level, 3)}`;
            blocks.push(
                <HeadingTag key={`heading-${blocks.length}`} className={styles[`markdownHeading${level}`]}>
                    {renderInlineMarkdown(headingMatch[2])}
                </HeadingTag>
            );
            continue;
        }

        const orderedMatch = line.match(/^\d+\.\s+(.*)$/);
        const bulletMatch = line.match(/^[-*]\s+(.*)$/);

        if (orderedMatch || bulletMatch) {
            flushParagraph();
            const nextListType = orderedMatch ? 'ordered' : 'unordered';
            if (listType && listType !== nextListType) {
                flushList();
            }
            listType = nextListType;
            listItems.push((orderedMatch || bulletMatch)[1]);
            continue;
        }

        if (line.startsWith('> ')) {
            flushParagraph();
            flushList();
            blocks.push(
                <blockquote key={`quote-${blocks.length}`} className={styles.markdownQuote}>
                    {renderInlineMarkdown(line.slice(2))}
                </blockquote>
            );
            continue;
        }

        paragraphLines.push(line);
    }

    flushParagraph();
    flushList();
    flushCode();

    return blocks;
};

// Optimization: Memoize markdown rendering
const MemoizedMarkdown = React.memo(({text}) => <>{renderMarkdown(text)}</>);
MemoizedMarkdown.displayName = 'MemoizedMarkdown';
MemoizedMarkdown.propTypes = {text: PropTypes.string};

const AISidebar = ({customContent, isOpen, onRequestClose}) => {
    const [prompt, setPrompt] = React.useState('');
    const [model, setModel] = React.useState('normal');
    const [isSending, setIsSending] = React.useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
    const [error, setError] = React.useState('');
    const [messages, setMessages] = React.useState([]);
    const [currentConversationId, setCurrentConversationId] = React.useState(null);
    const [conversations, setConversations] = React.useState(() => getStoredConversations());
    const [session, setSession] = React.useState(() => getStoredSession());
    const [credits, setCredits] = React.useState(() => getSessionCredits(getStoredSession()));

    React.useEffect(() => {
        const syncSession = () => {
            const storedSession = getStoredSession();
            setSession(storedSession);
            setCredits(getSessionCredits(storedSession));
        };

        syncSession();
        window.addEventListener('storage', syncSession);
        return () => window.removeEventListener('storage', syncSession);
    }, []);

    // Persist conversations
    React.useEffect(() => {
        if (messages.length > 0 && currentConversationId) {
            setConversations(prev => {
                const existing = prev.find(c => c.id === currentConversationId);
                let next;
                if (existing) {
                    next = prev.map(c => c.id === currentConversationId ? {...c, messages, lastUpdated: Date.now()} : c);
                } else {
                    const firstMsg = messages[0].text;
                    const title = firstMsg.slice(0, 32) + (firstMsg.length > 32 ? '...' : '');
                    next = [{id: currentConversationId, title, messages, lastUpdated: Date.now(), model}, ...prev];
                }
                setStoredConversations(next);
                return next;
            });
        }
    }, [messages, currentConversationId]);

    const handleNewConversation = React.useCallback(() => {
        setMessages([]);
        setError('');
        setPrompt('');
        setIsSending(false);
        setCurrentConversationId(Date.now().toString());
        setIsHistoryOpen(false);
    }, []);

    const handleSelectConversation = React.useCallback(conv => {
        setMessages(conv.messages);
        setCurrentConversationId(conv.id);
        setModel(conv.model || 'normal');
        setIsHistoryOpen(false);
        setError('');
    }, []);

    const handleDeleteConversation = React.useCallback((e, id) => {
        e.stopPropagation();
        setConversations(prev => {
            const next = prev.filter(c => c.id !== id);
            setStoredConversations(next);
            return next;
        });
        if (currentConversationId === id) {
            handleNewConversation();
        }
    }, [currentConversationId, handleNewConversation]);

    const handleSend = React.useCallback(async event => {
        event.preventDefault();
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || isSending) return;

        // Ensure we have an ID
        if (!currentConversationId) {
            setCurrentConversationId(Date.now().toString());
        }

        const cost = MODEL_COSTS[model] || MODEL_COSTS.normal;
        if (credits < cost) {
            setError(`You need ${cost} credits for ${model} mode, but only have ${credits}.`);
            return;
        }

        setIsSending(true);
        setError('');
        const nextMessages = [
            ...messages,
            {role: 'user', text: trimmedPrompt}
        ];
        setMessages(nextMessages);
        setPrompt('');

        try {
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    prompt: trimmedPrompt,
                    model,
                    history: nextMessages,
                    key: {
                        category: 'spiderIsBest'
                    }
                })
            });

            const contentType = response.headers.get('content-type') || '';
            let payload = null;

            if (contentType.includes('application/json')) {
                payload = await response.json();
            } else {
                payload = await response.text();
            }

            if (!response.ok) {
                const message = typeof payload === 'string'
                    ? payload
                    : payload && (payload.error || payload.message)
                        ? (payload.error || payload.message)
                        : `Request failed with status ${response.status}`;
                throw new Error(message);
            }

            const text = typeof payload === 'string'
                ? payload
                : payload && (payload.response || payload.answer || payload.text || payload.output)
                    ? (payload.response || payload.answer || payload.text || payload.output)
                    : JSON.stringify(payload, null, 2);

            const nextCredits = Math.max(0, credits - cost);
            setCredits(nextCredits);

            if (session?.access_token) {
                const nextMetadata = {
                    ...(session.user?.user_metadata || {}),
                    credits: nextCredits
                };
                const updatedUserPayload = await updateUser(session.access_token, nextMetadata);
                const updatedUser = updatedUserPayload?.user || updatedUserPayload;
                const nextSession = {
                    ...session,
                    user: updatedUser
                };
                setSession(nextSession);
                setStoredSession(nextSession);
            }

            setMessages(prevMessages => ([
                ...prevMessages,
                {role: 'assistant', text}
            ]));
        } catch (err) {
            const message = err && err.message ? err.message : 'Something went wrong sending that request.';
            setError(message);
            setMessages(prevMessages => ([
                ...prevMessages,
                {role: 'assistant', text: message, isError: true}
            ]));
        } finally {
            setIsSending(false);
        }
    }, [credits, currentConversationId, messages, model, prompt, session]);

    const handlePromptKeyDown = React.useCallback(event => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (prompt.trim() && !isSending) {
                handleSend(event);
            }
        }
    }, [handleSend, isSending, prompt]);

    return (
        <aside
            aria-hidden={!isOpen}
            className={classNames(styles.sidebar, {
                [styles.open]: isOpen
            })}
        >
            <button
                aria-label="Close AI sidebar"
                className={styles.scrim}
                tabIndex={isOpen ? 0 : -1}
                type="button"
                onClick={onRequestClose}
            />
            <section
                aria-label="BitByte AI sidebar"
                className={styles.panel}
                role="complementary"
            >
                    <header className={styles.header}>
                        <button
                            className={classNames(styles.historyToggleButton, {
                                [styles.historyActive]: isHistoryOpen
                            })}
                            title="Conversation History"
                            onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                        >
                        History
                        </button>
                    <div className={styles.headerText}>
                        <div className={styles.title}>BitByte AI</div>
                        <div className={styles.subtitle}>
                            Friendly help and ideas.
                        </div>
                    </div>
                    <button
                        className={styles.newConversationButton}
                        type="button"
                        onClick={handleNewConversation}
                    >
                        New
                    </button>
                    <button
                        aria-label="Close AI sidebar"
                        className={styles.closeButton}
                        type="button"
                        onClick={onRequestClose}
                    >
                        x
                    </button>
                </header>

                <div className={styles.body}>
                    {/* History Sidebar/Overlay */}
                    <div className={classNames(styles.historySidebar, {
                        [styles.historySidebarOpen]: isHistoryOpen
                    })}>
                        <div className={styles.sectionLabel}>Saved Chats</div>
                        <div className={styles.conversationsList}>
                            {conversations.length === 0 ? (
                                <div className={styles.emptyHistory}>No saved chats yet.</div>
                            ) : (
                                conversations.map(conv => (
                                    <div
                                        key={conv.id}
                                        className={classNames(styles.conversationItem, {
                                            [styles.conversationActive]: conv.id === currentConversationId
                                        })}
                                        onClick={() => handleSelectConversation(conv)}
                                    >
                                        <div className={styles.conversationTitle}>{conv.title}</div>
                                        <div className={styles.conversationDate}>
                                            {new Date(conv.lastUpdated).toLocaleDateString()}
                                        </div>
                                        <button
                                            className={styles.deleteConversationButton}
                                            onClick={e => handleDeleteConversation(e, conv.id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className={classNames(styles.intro, {
                        [styles.introHidden]: messages.length > 0 || isSending
                    })}>
                        <section className={styles.section}>
                            <div className={styles.sectionLabel}>Quick ideas</div>
                            <div className={styles.chipGrid}>
                                {quickIdeas.map(idea => (
                                    <button
                                        key={idea}
                                        className={styles.chipButton}
                                        type="button"
                                        onClick={() => setPrompt(idea)}
                                    >
                                        {idea}
                                    </button>
                                ))}
                            </div>
                        </section>

                        <section className={styles.section}>
                            <div className={styles.sectionLabel}>Custom zone</div>
                            <div
                                className={styles.customArea}
                                data-ai-sidebar-custom-area
                            >
                                {customContent || (
                                    <div className={styles.customAreaEmpty}>
                                        Drop your own AI buttons, chat logic, or tool output here.
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    <section className={styles.section}>
                        <div className={styles.sectionLabel}>Chat</div>
                        <div className={styles.chatStream}>
                            {messages.map((message, index) => (
                                <div
                                    key={`${message.role}-${index}-${message.text.slice(0, 12)}`}
                                    className={classNames(styles.chatBubble, {
                                        [styles.userBubble]: message.role === 'user',
                                        [styles.assistantBubble]: message.role === 'assistant',
                                        [styles.errorBubble]: message.isError
                                    })}
                                >
                                    <div className={styles.chatMeta}>
                                        <div className={classNames(styles.chatAvatar, {
                                            [styles.userAvatar]: message.role === 'user',
                                            [styles.assistantAvatar]: message.role === 'assistant'
                                        })}>
                                            {message.role === 'user' ? 'Y' : 'AI'}
                                        </div>
                                        <div className={styles.chatRole}>
                                            {message.role === 'user' ? 'You' : 'BitByte AI'}
                                        </div>
                                    </div>
                                    <div className={styles.chatText}>
                                        <MemoizedMarkdown text={message.text} />
                                    </div>
                                </div>
                            ))}
                            {isSending && (
                                <div className={classNames(styles.chatBubble, styles.assistantBubble, styles.thinkingBubble)}>
                                    <div className={styles.chatMeta}>
                                        <div className={classNames(styles.chatAvatar, styles.assistantAvatar)}>AI</div>
                                        <div className={styles.chatRole}>BitByte AI</div>
                                    </div>
                                    <div className={styles.thinkingText}>
                                        <span className={styles.thinkingPulse} />
                                        <span>BitByte AI is thinking...</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.sectionLabel}>Ask AI</div>
                        <form
                            className={styles.aiForm}
                            onSubmit={handleSend}
                        >
                            <label className={styles.fieldLabel} htmlFor="bitbyte-ai-model">
                                Model
                            </label>
                            <select
                                id="bitbyte-ai-model"
                                className={styles.select}
                                value={model}
                                onChange={event => setModel(event.target.value)}
                            >
                                {modelOptions.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>

                            <label className={styles.fieldLabel} htmlFor="bitbyte-ai-prompt">
                                Prompt
                            </label>
                            <textarea
                                id="bitbyte-ai-prompt"
                                className={styles.textarea}
                                placeholder="Ask BitByte AI..."
                                value={prompt}
                                onChange={event => setPrompt(event.target.value)}
                                onKeyDown={handlePromptKeyDown}
                            />

                            <button
                                className={styles.sendAction}
                                disabled={isSending || !prompt.trim()}
                                type="submit"
                            >
                                {isSending ? 'Sending...' : 'Send'}
                            </button>
                        </form>
                        {error ? (
                            <div className={styles.errorBox}>
                                {error}
                            </div>
                        ) : null}
                    </section>
                </div>
            </section>
        </aside>
    );
};

AISidebar.propTypes = {
    customContent: PropTypes.node,
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func.isRequired
};

AISidebar.defaultProps = {
    isOpen: false
};

export default AISidebar;
